/**
 * Labstep
 *
 * @module components/Entity/Action/Create
 * @desc Core create action. Creates entity on click.
 */

import { EntityCanCreateContainer } from 'labstep-web/containers/Entity/Create/Can';
import { ActionComponentModalContainer as ActionComponent } from 'labstep-web/core/Action/Component';
import type { APICallOptions } from 'labstep-web/models';
import {
  capitalize,
  getHumanReadableEntityName,
} from 'labstep-web/services/i18n.service';
import { navigation } from 'labstep-web/services/navigation';
import { callIf } from 'labstep-web/services/react.service';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import type React from 'react';
import { withRouter } from 'react-router-dom';
import type {
  EntityActionCreateContainerProps,
  EntityActionCreateProps,
} from './types';

export const EntityActionCreate: React.FC<EntityActionCreateProps> = (
  props,
) => {
  const {
    status,
    create,
    body = {},
    redirectRoute,
    entityName,
    customActionName,
    history,
    options = {},
    actionComponentProps,
    onClick,
    dataCy,
  } = props;

  let onSuccess: APICallOptions['onSuccess'];
  if (redirectRoute) {
    onSuccess = ({ response }): void => {
      if (!response) {
        return;
      }
      const { to, params, query } = redirectRoute({ response });
      history.push(navigation.get(to, params, false, query));
    };
  }

  return (
    <ActionComponent
      dataCy={dataCy}
      type="option"
      onClick={(e): void => {
        create(body as any, enhanceOptions({ options, onSuccess }));
        callIf(onClick, e);
      }}
      status={status}
      text={
        customActionName ||
        `New ${capitalize(
          getHumanReadableEntityName(
            entityName,
            false,
            false,
            body.is_template as boolean,
          ),
        )}`
      }
      icon={
        actionComponentProps?.showIcon === undefined ||
        actionComponentProps?.showIcon === true
          ? 'plus'
          : undefined
      }
      {...actionComponentProps}
    />
  );
};

export const EntityActionCreateContainer: React.FC<
  EntityActionCreateContainerProps
> = ({
  entityName,
  parentName,
  parentId,
  overwriteCanCreateProps = {},
  skipPermissionCheck,
  redirectRoute,
  batch,
  ...rest
}) => (
  <EntityCanCreateContainer
    entityName={entityName}
    parentName={parentName}
    parentId={parentId}
    isTemplate={!!rest.body?.is_template}
    overwriteCanCreateProps={{
      entityName: rest.body?.is_template
        ? `${entityName}_template`
        : entityName,
      ...overwriteCanCreateProps,
    }}
    skipPermissionCheck={skipPermissionCheck}
    showLoaderWhileCreating={redirectRoute !== undefined}
    batch={batch}
  >
    {({ status, create }) => (
      <EntityActionCreate
        {...rest}
        status={status}
        create={create}
        entityName={entityName}
        redirectRoute={redirectRoute}
      />
    )}
  </EntityCanCreateContainer>
);

export default withRouter(EntityActionCreateContainer);
